import { toastSuccess } from 'libs/toast/toastSucess'
import { toastFailure } from 'libs/toast/toastFailure'
import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useEditPropertyMutation } from '../products-endpoints/products.endpoints'
import { AnimatePresence } from 'framer-motion'
import { motion } from 'framer-motion'
import { dropdownMotionProps } from 'utils/FramerProps'
import ArrowDown from 'assets/icons/camera-page/ArrowDown'
import { trimStringValues } from 'utils/stringHandlers'

const drop1 = [
  {
    title: 'Measurable Property',
    value: 'Measurable Property',
    subtitle: 'Use this for any measurement (e.g., weight in Kg).',
  },
  {
    title: 'Fixed Property',
    subtitle: 'Specify unchanging product attributes (e.g., color = black).',
  },
]
const drop2 = [
  {
    title: 'Count',
  },
  {
    title: 'Density',
  },
  {
    title: 'Frequency',
  },
  {
    title: 'Height / Width / Depth',
  },
  {
    title: 'Pressure',
  },
  {
    title: 'Temperature',
  },
  {
    title: 'Weight',
  },
]

const EditProperty = ({
  removeEntry,
  editTableData,
  data,
  toggleDelete,
  onPropertyChange,
}: any) => {
  const location = useLocation()
  const [formData, setFormData] = useState(data)
  const [errors, setErrors] = useState<any>({})
  const [hasUnits, setHasUnits] = useState(formData?.unit ? true : false)
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const [isDropdownVisible2, setIsDropdownVisible2] = useState(false)
  const [selectedTitle, setSelectedTitle] = useState(data?.type)
  const [selectedTitle2, setSelectedTitle2] = useState(data?.value)
  const [editProperty, { isLoading: propertyLoading }] =
    useEditPropertyMutation()

  const handleTitleClick = (title: any) => {
    setSelectedTitle(title)
    setIsDropdownVisible(false)

    setErrors({ ...errors, propertyType: '' })
  }
  const handleTitleClick2 = (title: any) => {
    setSelectedTitle2(title)
    setIsDropdownVisible2(false)

    // Clear the propertyType2 error when a title is selected for the second dropdown
    setErrors({ ...errors, measurementType: '' })
  }

  const handleCheckboxChange = (event: any) => {
    const isChecked = event.target.checked
    setHasUnits(isChecked)
  }

  const handleInputChange = (e: any) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setErrors({ ...errors, [name]: '' })
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()
    const newErrors: any = {}

    // Check each field for errors
    if (formData.name.trim() === '') {
      newErrors.name = 'Property Name cannot be empty.'
    }
    if (selectedTitle === '') {
      newErrors.propertyType = 'Property Type cannot be empty.'
    }
    if (selectedTitle2 === '') {
      newErrors.measurementType = 'Measurement Type cannot be empty.'
    }

    // Set the new errors
    setErrors(newErrors)

    // If there are no errors, submit the form
    if (
      Object.keys(newErrors).length === 0 &&
      location.pathname === '/products/newProducts'
    ) {
      const newProperty = {
        id: formData.id,
        name: formData?.name,
        type: selectedTitle,
        value: selectedTitle2,
        unit: formData?.unit,
      }

      const data = trimStringValues(newProperty)

      editTableData(data)

      setFormData({
        name: '',
        selectedTitle2: '',
        selectedTitle: '',
      })

      // Uncheck the "This property has units" checkbox
      toastSuccess(`Property "${formData?.name}" added successfully`)
      setHasUnits(false)
      removeEntry()
    } else if (
      Object.keys(newErrors).length === 0 &&
      location.pathname === '/products/editProducts'
    ) {
      const newProperty = {
        id: formData.id,
        name: formData?.name,
        type: selectedTitle,
        value: selectedTitle2,
        unit: formData?.unit,
      }

      const data = trimStringValues(newProperty)

      editProperty({ data: data, propertyId: formData.id })
        .unwrap()
        .then(() => {
          toastSuccess(`Property "${formData?.name}" is updated successfully.`)
          removeEntry()
          onPropertyChange(true)
        })
        .catch((err) => {
          toastFailure(
            err?.data?.detail ?? 'Failed to add property. Please try again.',
          )
        })
    }
  }

  const popupRef = useRef(null)

  useEffect(() => {
    const handleOutsideClick = (event: any) => {
      // Check if the click is outside the popup
      if (popupRef.current && !event.target.closest('.ignoreref')) {
        setIsDropdownVisible(false)
        setIsDropdownVisible2(false)
      }
    }

    // Adding event listener to detect clicks outside the popups
    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      // Clean up the event listener when the component unmounts
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const handleInputClick = () => {
    setIsDropdownVisible(!isDropdownVisible)
    setIsDropdownVisible2(false)
  }

  const handleInputClick2 = () => {
    setIsDropdownVisible2(!isDropdownVisible2)
    setIsDropdownVisible(false)
  }

  return (
    <div>
      <div>
        <form onSubmit={handleSubmit}>
          <div>
            <label className="mb-1 p-sm-regular">
              Property Name <span className="text-[#DC3545]">*</span>
            </label>
            <div>
              <div className="relative py-1">
                <input
                  required
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`h-[28px] w-full border ${
                    errors.name ? 'border-[#DC3545]' : 'border-[#E0E0E0]'
                  } rounded-md border border-solid p-2 pr-8 text-sm outline-none p-xs-regular`}
                  placeholder="Enter property name (e.g., Color)"
                />
                {errors.name && (
                  <p className="pt-1 text-xs font-normal text-[#DC3545]">
                    {errors.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="relative mt-5">
              <label className="mb-1 p-sm-regular">
                Property Type <span className="text-[#DC3545]">*</span>
              </label>
              <input
                required
                name="propertyType"
                type="text"
                autoComplete="off"
                className={`h-[28px] w-full ${
                  errors.propertyType ? 'border-[#DC3545]' : 'border-[#E0E0E0]'
                } cursor-pointer rounded-md 
         border border-solid p-2 pr-8 text-sm outline-none p-xs-regular focus:border-[#1193F0]`}
                placeholder="Select property type"
                onClick={handleInputClick}
                value={selectedTitle}
              />
              <div className="absolute right-[0.5rem] top-[1.9rem]">
                <ArrowDown color="" />
              </div>
              {errors.propertyType && (
                <p className="pt-1 text-xs font-normal text-[#DC3545]">
                  {errors.propertyType}
                </p>
              )}
              <AnimatePresence>
                {isDropdownVisible && (
                  <motion.div
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={dropdownMotionProps}
                    className="absolute min-h-[20px] w-[378px] rounded bg-[#fff]"
                    style={{
                      zIndex: '3',
                      boxShadow:
                        '0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 1px 8px 0px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    {drop1.map((item) => (
                      <div
                        key={item.title}
                        className="cursor-pointer flex-col gap-3 border-b border-[#EBEFF3] px-3 py-2 hover:bg-[#f8f8f8]"
                        onClick={() => handleTitleClick(item.value)}
                      >
                        <div className="p-xs-regular">{item.title}</div>
                        <div className="p-xs-regular-dim">{item.subtitle}</div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {selectedTitle === 'Measurable Property' && (
            <>
              <div className="relative mt-5">
                <label className="mb-1 p-sm-regular">
                  Measurement Type <span className="text-[#DC3545]">*</span>
                </label>
                <input
                  required
                  name="measurementType"
                  type="text"
                  className={`h-[28px] w-full ${
                    errors.measurementType
                      ? 'border-[#DC3545]'
                      : 'border-[#E0E0E0]'
                  } cursor-pointer rounded-md 
         border border-solid p-2 pr-8 text-sm outline-none p-xs-regular focus:border-[#1193F0]`}
                  placeholder="Select measure type"
                  onClick={handleInputClick2}
                  value={selectedTitle2}
                />
                <div className="absolute right-[0.5rem] top-[1.9rem]">
                  <ArrowDown color="" />
                </div>
                {errors.measurementType && (
                  <p className="pt-1 text-xs font-normal text-[#DC3545]">
                    {errors.measurementType}
                  </p>
                )}
                <AnimatePresence>
                  {isDropdownVisible2 && (
                    <motion.div
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={dropdownMotionProps}
                      className="absolute min-h-[20px] w-[378px] rounded bg-[#fff]"
                      style={{
                        zIndex: '3',
                        boxShadow:
                          '0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 1px 8px 0px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      {drop2.map((item) => (
                        <div
                          key={item.title}
                          className="cursor-pointer flex-col gap-3 border-b border-[#EBEFF3] px-3 py-2 hover:bg-[#f8f8f8]"
                          onClick={() => handleTitleClick2(item.title)}
                        >
                          <div className="p-xs-regular">{item.title}</div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="mb-1.5 mt-5 flex gap-2 p-sm-regular">
                <input
                  type="checkbox"
                  checked={hasUnits}
                  onChange={handleCheckboxChange}
                />
                <p>This property has units</p>
              </div>
            </>
          )}
          {hasUnits && (
            <div>
              <input
                required
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
                className={`h-[28px] w-full rounded-md border border-solid border-[#E0E0E0] p-2 pr-8 text-sm outline-none p-xs-regular`}
                placeholder="Enter Units"
              />
            </div>
          )}

          <div className="mb-8 mt-6 flex justify-between gap-2">
            <div>
              <button
                type="button"
                className="p-md-bold btn-delete"
                onClick={() => {
                  toggleDelete(data)
                }}
              >
                Delete
              </button>
            </div>
            <div className="flex gap-2">
              <button
                className="btn-cancel"
                type="button"
                onClick={removeEntry}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={propertyLoading}
                onClick={handleSubmit}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditProperty
